import { useMemo, useState } from "react";
import {
  FiCalendar,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCopy,
  FiExternalLink,
  FiMapPin,
  FiRss,
  FiUsers,
  FiVideo,
  FiX,
} from "react-icons/fi";
import useSWR from "swr";
import { get, post } from "../../../../api";
import Modal from "../../../cmp/Modal";
import StyledButton from "../../../cmp/themed/StyledButton";

interface CalenderItemConfig {
  id: string;
  title: string;
  description: string;
  allow_rsvp: boolean;
  rsvp_limit?: number;
  has_rsvp: boolean;
  start_ts: number; // in seconds since epoch
  end_ts: number; // in seconds since epoch
  banner?: {
    url: string;
  };
  meeting_url?: string | null;
  location_url?: string | null;

  files?: Array<{
    url: string;
    metadata: {
      thumbnail_url?: string;
      width?: number;
      height?: number;
      host: "bunny" | "youtube" | "direct";
      videoId?: string; // for youtube or vimeo or bunny video id
      libraryId?: string; // for bunny library id
    };
    name: string;
    filetype: string; // image/jpeg, video/mp4, application/pdf, etc.
    type: "general" | "recording";
  }>; // these are the recordings of the meeting
}

interface CalenderConfig {
  id: string;
  name: string;
  items: CalenderItemConfig[];
}

function formatDate(ts: number) {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDay(ts: number) {
  return new Date(ts * 1000).getDate();
}

function getMonthShort(ts: number) {
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: "short" });
}

function formatTime(ts: number) {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isUpcoming(end_ts: number) {
  return end_ts * 1000 > Date.now();
}

function EventDetailModal({
  event,
  onClose,
  onRsvpChange,
}: {
  event: CalenderItemConfig;
  onClose: () => void;
  onRsvpChange: (id: string, rsvped: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);

  async function handleRsvp() {
    setLoading(true);
    try {
      if (event.has_rsvp) {
        await post(`/community/events/${event.id}/rsvp/cancel`, {});
        onRsvpChange(event.id, false);
      } else {
        await post(`/community/events/${event.id}/rsvp`, {});
        onRsvpChange(event.id, true);
      }
    } finally {
      setLoading(false);
    }
  }

  const upcoming = isUpcoming(event.end_ts);

  return (
    <Modal visible={true} onClose={onClose} title={event.title} size="lg">
      {event.banner?.url && (
        <img
          src={event.banner.url}
          alt={event.title}
          className="rounded mb-3 w-100"
          style={{ maxHeight: 240, objectFit: "cover" }}
        />
      )}

      <div className="d-flex flex-wrap gap-3 mb-3">
        <span className="d-flex align-items-center gap-1 text-muted small">
          <FiCalendar size={14} />
          {formatDate(event.start_ts)}
        </span>
        <span className="d-flex align-items-center gap-1 text-muted small">
          <FiClock size={14} />
          {formatTime(event.start_ts)} – {formatTime(event.end_ts)}
        </span>
        {event.rsvp_limit > 0 && (
          <span className="d-flex align-items-center gap-1 text-muted small">
            <FiUsers size={14} />
            {event.rsvp_limit} spots
          </span>
        )}
      </div>

      {event.description && (
        <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
          {event.description}
        </p>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        {event.meeting_url && (
          <a
            href={event.meeting_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          >
            <FiVideo size={13} /> Join Meeting <FiExternalLink size={12} />
          </a>
        )}
        {event.location_url && (
          <a
            href={event.location_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          >
            <FiMapPin size={13} /> View Location <FiExternalLink size={12} />
          </a>
        )}
      </div>

      {event.allow_rsvp && upcoming && (
        <StyledButton
          className={`btn d-flex align-items-center gap-1 ${event.has_rsvp ? "btn-outline-danger" : "btn"}`}
          loading={loading}
          onClick={handleRsvp}
        >
          {event.has_rsvp ? (
            <>
              <FiX size={14} /> Cancel RSVP
            </>
          ) : (
            <>
              <FiCheck size={14} /> RSVP to this event
            </>
          )}
        </StyledButton>
      )}
    </Modal>
  );
}

function EventCard({
  event,
  onClick,
}: {
  event: CalenderItemConfig;
  onClick: () => void;
}) {
  const upcoming = isUpcoming(event.end_ts);

  return (
    <div
      className="card h-100 shadow-sm border-0"
      style={{
        cursor: "pointer",
        opacity: upcoming ? 1 : 0.7,
        overflow: "hidden",
      }}
      onClick={onClick}
    >
      <div className="position-relative">
        {event.banner?.url ? (
          <img
            src={event.banner.url}
            alt={event.title}
            className="card-img-top"
            style={{ height: 160, objectFit: "cover" }}
          />
        ) : (
          <div
            className="card-img-top d-flex align-items-center justify-content-center bg-light text-muted"
            style={{ height: 160 }}
          >
            <FiCalendar size={40} />
          </div>
        )}
        <div
          className="position-absolute"
          style={{
            top: 12,
            left: 12,
            backgroundColor: "#fff",
            borderRadius: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            overflow: "hidden",
            minWidth: 52,
            textAlign: "center",
          }}
        >
          <div
            style={{
              backgroundColor: upcoming ? "#dc3545" : "#6c757d",
              color: "#fff",
              fontSize: "0.68rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              padding: "2px 6px",
              lineHeight: 1.4,
            }}
          >
            {getMonthShort(event.start_ts)}
          </div>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#212529",
              padding: "4px 6px 5px",
              lineHeight: 1,
            }}
          >
            {String(getDay(event.start_ts)).padStart(2, "0")}
          </div>
        </div>
      </div>
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <h6 className="card-title fw-semibold mb-0">{event.title}</h6>
          {!upcoming && (
            <span className="badge bg-secondary ms-2 flex-shrink-0">Past</span>
          )}
          {upcoming && event.has_rsvp && (
            <span className="badge bg-success ms-2 flex-shrink-0 d-flex align-items-center gap-1">
              <FiCheck size={10} /> RSVP'd
            </span>
          )}
        </div>

        <div className="text-muted small mb-2 d-flex align-items-center gap-1">
          <FiClock size={12} />
          {formatTime(event.start_ts)}
        </div>

        {event.description && (
          <p
            className="card-text text-muted small mb-3"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {event.description}
          </p>
        )}

        <div className="mt-auto d-flex align-items-center gap-2 flex-wrap">
          {event.meeting_url && (
            <span className="badge bg-primary-subtle text-primary d-flex align-items-center gap-1">
              <FiVideo size={10} /> Online
            </span>
          )}
          {event.location_url && (
            <span className="badge bg-secondary-subtle text-secondary d-flex align-items-center gap-1">
              <FiMapPin size={10} /> In-person
            </span>
          )}
          {event.allow_rsvp && upcoming && (
            <span className="badge bg-info-subtle text-info d-flex align-items-center gap-1">
              <FiUsers size={10} /> RSVP
            </span>
          )}
          {event.files && event.files.length > 0 && (
            <span className="badge bg-success-subtle text-success d-flex align-items-center gap-1">
              <FiVideo size={10} /> {event.files.length} recording
              {event.files.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {upcoming && (
          <a
            href={`webcal://app.goaffpro.com/ical/event/${event.id}/download`}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 mt-2 w-100 justify-content-center"
            onClick={(e) => e.stopPropagation()}
          >
            <FiCalendar size={12} /> Add to Calendar
          </a>
        )}
      </div>
    </div>
  );
}

const MOCK_WEBCAL_BASE = `webcal://app.goaffpro.com/ical/calendar/mock-token`;

function CalendarViewModal({
  events,
  onClose,
  onEventClick,
}: {
  events: CalenderItemConfig[];
  onClose: () => void;
  onEventClick: (event: CalenderItemConfig) => void;
}) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth()); // 0-indexed

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalenderItemConfig[]> = {};
    events.forEach((e) => {
      const d = new Date(e.start_ts * 1000);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] ??= []).push(e);
    });
    return map;
  }, [events]);

  function prevMonth() {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else {
      setMonth((m) => m - 1);
    }
  }

  function nextMonth() {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else {
      setMonth((m) => m + 1);
    }
  }

  const monthLabel = new Date(year, month).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const todayKey = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  })();

  return (
    <Modal visible={true} onClose={onClose} title="Calendar View" size="lg">
      {/* Month nav */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={prevMonth}
        >
          <FiChevronLeft />
        </button>
        <h6 className="fw-semibold mb-0">{monthLabel}</h6>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={nextMonth}
        >
          <FiChevronRight />
        </button>
      </div>

      {/* Day headers */}
      <div className="row g-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="col text-center text-muted fw-semibold"
            style={{
              fontSize: "0.72rem",
              width: `${100 / 7}%`,
              flex: "0 0 auto",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="row g-1">
        {/* Empty cells before first day */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="col"
            style={{ width: `${100 / 7}%`, flex: "0 0 auto" }}
          />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = `${year}-${month}-${day}`;
          const dayEvents = eventsByDay[key] ?? [];
          const isToday = key === todayKey;

          return (
            <div
              key={day}
              className="col border rounded-1 p-1"
              style={{
                width: `${100 / 7}%`,
                flex: "0 0 auto",
                minHeight: 72,
                backgroundColor: isToday ? "#f0f7ff" : undefined,
                borderColor: isToday ? "#0d6efd" : undefined,
              }}
            >
              <div
                className={`text-end small fw-semibold mb-0 ${isToday ? "text-primary" : "text-muted"}`}
                style={{ fontSize: "0.7rem" }}
              >
                {isToday ? (
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                    style={{ width: 20, height: 20, fontSize: "0.65rem" }}
                  >
                    {day}
                  </span>
                ) : (
                  day
                )}
              </div>
              {dayEvents.slice(0, 2).map((ev) => (
                <div
                  key={ev.id}
                  className="text-truncate rounded-1 px-1 mt-0 small"
                  style={{
                    fontSize: "0.62rem",
                    cursor: "pointer",
                    backgroundColor: isUpcoming(ev.end_ts)
                      ? "#cfe2ff"
                      : "#e9ecef",
                    color: isUpcoming(ev.end_ts) ? "#084298" : "#495057",
                    lineHeight: 1.4,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(ev);
                  }}
                  title={ev.title}
                >
                  {ev.title}
                </div>
              ))}
              {dayEvents.length > 2 && (
                <div
                  className="text-muted"
                  style={{ fontSize: "0.6rem", lineHeight: 1 }}
                >
                  +{dayEvents.length - 2} more
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function SubscribeModal({ onClose }: { onClose: () => void }) {
  const [rsvpOnly, setRsvpOnly] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = rsvpOnly ? `${MOCK_WEBCAL_BASE}?rsvp=1` : MOCK_WEBCAL_BASE;

  function handleCopy() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal visible={true} onClose={onClose} title="Subscribe to Calendar">
      <p className="text-muted small mb-3">
        Add these events to your calendar app. The feed stays in sync
        automatically.
      </p>

      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="rsvp-only-toggle"
          checked={rsvpOnly}
          onChange={(e) => setRsvpOnly(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="rsvp-only-toggle">
          Only include events I've RSVP'd to
        </label>
      </div>

      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control form-control-sm font-monospace"
          value={url}
          readOnly
        />
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <FiCheck size={13} /> Copied
            </>
          ) : (
            <>
              <FiCopy size={13} /> Copy
            </>
          )}
        </button>
      </div>

      <a
        href={url}
        className="btn btn-sm d-flex align-items-center gap-1 w-100 justify-content-center"
      >
        <FiRss size={13} /> Open in Calendar App
      </a>

      <p className="text-muted mt-3 mb-0" style={{ fontSize: "0.75rem" }}>
        Works with Apple Calendar, Google Calendar, Outlook, and any app that
        supports iCal / webcal.
      </p>
    </Modal>
  );
}

export default function CalendarEvents({ calendarId }: { calendarId: string }) {
  const { data, mutate } = useSWR<CalenderConfig>(
    `/community/calendars/${calendarId}`,
    get,
  );
  const [selectedEvent, setSelectedEvent] = useState<CalenderItemConfig | null>(
    null,
  );
  const [filter, setFilter] = useState<"all" | "upcoming" | "past">("upcoming");
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [calendarViewOpen, setCalendarViewOpen] = useState(false);

  const items = data?.items ?? [];
  const filtered = items.filter((e) => {
    if (filter === "upcoming") return isUpcoming(e.end_ts);
    if (filter === "past") return !isUpcoming(e.end_ts);
    return true;
  });

  function handleRsvpChange(id: string, rsvped: boolean) {
    mutate(
      (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((e) =>
                e.id === id ? { ...e, has_rsvp: rsvped } : e,
              ),
            }
          : prev,
      false,
    );
    if (selectedEvent?.id === id) {
      setSelectedEvent((prev) => (prev ? { ...prev, has_rsvp: rsvped } : prev));
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
          <FiCalendar /> {data?.name ?? "Events"}
        </h5>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => setCalendarViewOpen(true)}
            title="Calendar View"
          >
            <FiCalendar size={13} />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => setSubscribeOpen(true)}
          >
            <FiRss size={13} /> Subscribe
          </button>
          <div className="btn-group btn-group-sm">
            {(["upcoming", "all", "past"] as const).map((f) => (
              <button
                key={f}
                className={`btn ${filter === f ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <FiCalendar size={40} className="mb-3 opacity-50" />
          <p className="mb-0">
            No {filter !== "all" ? filter : ""} events found.
          </p>
        </div>
      ) : (
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {filtered.map((event) => (
            <div key={event.id} className="col">
              <EventCard
                event={event}
                onClick={() => setSelectedEvent(event)}
              />
            </div>
          ))}
        </div>
      )}

      {calendarViewOpen && (
        <CalendarViewModal
          events={items}
          onClose={() => setCalendarViewOpen(false)}
          onEventClick={(event) => {
            setCalendarViewOpen(false);
            setSelectedEvent(event);
          }}
        />
      )}

      {subscribeOpen && (
        <SubscribeModal onClose={() => setSubscribeOpen(false)} />
      )}

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onRsvpChange={handleRsvpChange}
        />
      )}
    </div>
  );
}
